import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, tokenStore } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) { setUser(null); return null; }
    try {
      const me = await api.me();
      setUser(me);
      return me;
    } catch {
      tokenStore.clear();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setReady(true);
    })();
  }, [refresh]);

  const login = async ({ identifier, email, password }) => {
    // Accept either `identifier` (email OR mobile) or legacy `email` key
    const res = await api.login({ identifier: identifier || email, password });
    tokenStore.set(res.token);
    setUser(res.user);
    return res.user;
  };

  const signup = async ({ email, password, name, mobile }) => {
    const res = await api.signup({ email, password, name, mobile });
    tokenStore.set(res.token);
    setUser(res.user);
    return res.user;
  };

  const googleAuth = async ({ email, name }) => {
    const res = await api.google({ email, name });
    tokenStore.set(res.token);
    setUser(res.user);
    return res.user;
  };

  const completeOnboarding = async (answers) => {
    const updated = await api.submitOnboarding(answers);
    setUser(updated);
    return updated;
  };

  const update = async (patch) => {
    // Optimistic update
    setUser((u) => (u ? { ...u, ...patch } : u));
    // Persist selected keys to server
    try {
      const allowed = ['name', 'email', 'city', 'country'];
      const serverPatch = Object.fromEntries(Object.entries(patch).filter(([k]) => allowed.includes(k)));
      if (Object.keys(serverPatch).length > 0) {
        const updated = await api.updateMe(serverPatch);
        setUser(updated);
        return updated;
      }
    } catch {
      // ignore transient errors; the optimistic state remains
    }
    return null;
  };

  const logout = () => { tokenStore.clear(); sessionStorage.removeItem('tbt_signup_draft_v1'); setUser(null); };

  return (
    <AuthContext.Provider value={{ user, ready, login, signup, googleAuth, logout, completeOnboarding, update, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
